import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * How much fixed chrome is docked at the bottom of the viewport right now.
 *
 * The phone tab bar lives in Shell, the now-playing bar lives in player.tsx,
 * and they are siblings — neither can see the other, so each used to spell the
 * other's height as a literal (`3.75rem`, three times, plus a `pb-16` and an
 * `h-20` that matched neither). A bar registers its own height here; anything
 * that must dock above the chrome, or leave room at the end of a page, reads
 * the total back.
 *
 * Heights are CSS lengths, not numbers: the tab bar's is `3.75rem` plus a safe
 * area inset that only the browser can resolve. They compose with `calc`.
 */

interface ChromeApi {
  /** Every registered bottom bar's height, as CSS lengths. */
  heights: readonly string[];
  register: (id: string, height: string) => void;
  clear: (id: string) => void;
}

const ChromeContext = createContext<ChromeApi | null>(null);

export function BottomChromeProvider({ children }: { children: ReactNode }) {
  const [bars, setBars] = useState<Record<string, string>>({});

  const register = useCallback((id: string, height: string) => {
    setBars((prev) => (prev[id] === height ? prev : { ...prev, [id]: height }));
  }, []);

  const clear = useCallback((id: string) => {
    setBars((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const heights = useMemo(() => Object.values(bars), [bars]);
  const api = useMemo<ChromeApi>(() => ({ heights, register, clear }), [heights, register, clear]);

  return <ChromeContext.Provider value={api}>{children}</ChromeContext.Provider>;
}

function useChrome(): ChromeApi | null {
  return useContext(ChromeContext);
}

/**
 * Declare that this component renders a fixed bar of `height` at the bottom of
 * the viewport, for as long as it is mounted. `height` may be null when the bar
 * is not currently showing (a phone-only bar on a desktop render, say).
 */
export function useRegisterBottomChrome(id: string, height: string | null): void {
  const chrome = useChrome();
  const register = chrome?.register;
  const clear = chrome?.clear;
  useEffect(() => {
    if (!register || !clear) return;
    if (height === null) {
      clear(id);
      return;
    }
    register(id, height);
    return () => clear(id);
  }, [id, height, register, clear]);
}

/**
 * The tallest bottom bar on screen, as a CSS length — `0px` when there is
 * none. `max()` rather than a sum: the bars overlap the same corner of the
 * screen, they do not stack.
 */
export function useBottomChrome(): string {
  const chrome = useChrome();
  const heights = chrome?.heights ?? [];
  if (heights.length === 0) return "0px";
  if (heights.length === 1) return heights[0]!;
  return `max(${heights.join(", ")})`;
}
